import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, LogOut, FileText, ShieldCheck, Mail, HelpCircle, ChevronRight, Loader2, Ban, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DEFAULT_SUPPORT_EMAIL = "support@nosey.ai";

const DEFAULT_TERMS = `By using Nosey AI you agree to generate content responsibly and lawfully. You retain ownership of the media you upload and the outputs you create, subject to the licenses of the underlying AI providers. Credits purchased are used to power image and video generation and are non-refundable once consumed. You may not use the service to create content that is illegal, infringes on others' rights, or violates the acceptable-use policies of our model providers. We may update these terms from time to time; continued use constitutes acceptance of the latest version.`;

const DEFAULT_PRIVACY = `We collect only what we need to run Nosey AI: your account email, the media you upload for generation, and the creations you produce. Uploaded references and prompts are sent to our AI providers (OpenAI, Google, and SeeDance) solely to fulfil your generation requests. We do not sell your personal data. You can delete your creations at any time from the Creations screen. For account deletion or data requests, contact us at ${DEFAULT_SUPPORT_EMAIL}.`;

const FAQ = [
  { q: "What uses credits?", a: "Editing, writing prompts and building storyboards are free. Only image and video generation consume credits." },
  { q: "Why is my video still processing?", a: "Animations render asynchronously and can take up to a couple of minutes depending on length and resolution. It will appear in Creations once ready." },
  { q: "How do I keep a character consistent?", a: "Upload a reference image and tag it as a Character. Nosey locks the identity using image conditioning across your generations." },
  { q: "How do I add more credits?", a: "Open Settings → Subscription Management (or the Upgrade tab) to top up." },
];

function Row({ icon: Icon, label, sub, onClick, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex items-center gap-4 px-4 py-4 hover:bg-white/[0.03] transition-colors text-left ${danger ? "text-red-400" : "text-[#F0E9DD]"}`}>
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center border ${danger ? "border-red-500/30 bg-red-500/10" : "border-gold/25 bg-[#1a140d]"}`}>
        <Icon size={18} className={danger ? "text-red-400" : "text-gold"} />
      </span>
      <span className="flex-1">
        <span className="block font-heading font-semibold text-sm">{label}</span>
        {sub && <span className="block text-xs text-zinc-500 mt-0.5">{sub}</span>}
      </span>
      <ChevronRight size={18} className="text-zinc-600" />
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="label-mono px-1 mb-2">{title}</div>
      <div className="rounded-2xl border border-white/10 bg-ink-900/60 divide-y divide-white/[0.06] overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function MembershipCard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/membership/status").then(({ data }) => setM(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const sid = params.get("membership_session");
    if (!sid) return;
    api.get(`/membership/status/${sid}`).then(({ data }) => { setM(data); toast.success("Membership active — thank you!"); params.delete("membership_session"); setParams(params, { replace: true }); }).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const activate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/membership/checkout", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch { toast.error("Couldn't start checkout"); setBusy(false); }
  };
  if (!m) return null;
  const label = { active: "Active", trial: "Free trial", grace: "Payment due", expired: "Expired" }[m.status] || m.status;
  const color = m.status === "active" ? "text-green-400" : m.status === "trial" ? "text-gold" : "text-red-400";
  const showCta = m.status !== "active";
  return (
    <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-[#1a140d] to-ink-900 p-5" data-testid="membership-card">
      <div className="flex items-center justify-between">
        <div className="font-heading font-black text-lg">Nosey Membership</div>
        <span className={`text-xs font-heading font-bold ${color}`} data-testid="membership-status">{label}{m.days_left != null ? ` · ${m.days_left}d` : ""}</span>
      </div>
      <p className="text-xs text-zinc-400 mt-1">
        {m.status === "trial" && `Your free week is running. It becomes $0.99/year after the trial.`}
        {m.status === "grace" && `Your membership lapsed. Renew within the grace period to keep your account — inactive accounts are removed after 30 days.`}
        {m.status === "expired" && `Your membership expired. Renew now to keep your account active.`}
        {m.status === "active" && `Your $0.99/year social membership is active. Kept separate from coins & AI credits.`}
      </p>
      {showCta && (
        <button onClick={activate} disabled={busy} data-testid="membership-activate" className="mt-3 w-full rounded-lg bg-gold text-ink-950 font-heading font-black py-2.5 disabled:opacity-60 flex items-center justify-center gap-2">
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Activate — $0.99/year"}
        </button>
      )}
    </div>
  );
}


export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [doc, setDoc] = useState(null); // 'terms' | 'privacy' | 'help'
  const [cfg, setCfg] = useState(null);
  const [blocksOpen, setBlocksOpen] = useState(false);

  useEffect(() => { api.get("/site-config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);

  const SUPPORT_EMAIL = cfg?.contact_email || DEFAULT_SUPPORT_EMAIL;
  const TERMS = cfg?.terms || DEFAULT_TERMS;
  const PRIVACY = cfg?.privacy || DEFAULT_PRIVACY;
  const CONTACT_MSG = cfg?.contact_message || "";

  const signOut = () => { logout(); toast.success("Signed out"); navigate("/"); };

  return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD] pb-28">
      <div className="sticky top-0 z-30 bg-ink-950/85 backdrop-blur-xl px-5 pt-6 pb-3 border-b border-white/5">
        <div className="max-w-2xl mx-auto">
          <h1 className="font-heading font-black uppercase tracking-tighter text-3xl">Settings</h1>
          {user?.email && <p className="text-sm text-zinc-500 mt-1" data-testid="settings-email">{user.email}</p>}
        </div>
      </div>

      <motion.main initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="max-w-2xl mx-auto px-5 pt-6 space-y-7" data-testid="settings-page">
        <MembershipCard />
        <Section title="Account">
          <Row icon={CreditCard} label="Subscription Management" sub={user ? `${user.credits ?? 0} credits available` : "Manage your plan & credits"}
            onClick={() => navigate("/pricing")} testid="settings-subscription" />
          <Row icon={LogOut} label="Sign Out" danger onClick={signOut} testid="settings-signout" />
        </Section>

        <Section title="Privacy & Safety">
          <Row icon={Ban} label="Blocked accounts" sub="People you've blocked from messaging you"
            onClick={() => setBlocksOpen(true)} testid="settings-blocked" />
        </Section>

        <Section title="Legal">
          <Row icon={FileText} label="Terms & Conditions" onClick={() => setDoc("terms")} testid="settings-terms" />
          <Row icon={ShieldCheck} label="Privacy Policy" onClick={() => setDoc("privacy")} testid="settings-privacy" />
        </Section>

        <Section title="Support">
          <Row icon={Mail} label="Contact" sub={SUPPORT_EMAIL}
            onClick={() => { window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Nosey%20AI%20Support`; }} testid="settings-contact" />
          <Row icon={HelpCircle} label="Help / Questions" onClick={() => setDoc("help")} testid="settings-help" />
        </Section>

        <p className="text-center text-xs text-zinc-600 pt-2">Nosey AI · v1.0</p>
      </motion.main>

      <Dialog open={!!doc} onOpenChange={(o) => !o && setDoc(null)}>
        <DialogContent className="bg-ink-900 border-zinc-800 text-[#F0E9DD] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase tracking-tight">
              {doc === "terms" ? "Terms & Conditions" : doc === "privacy" ? "Privacy Policy" : "Help & Questions"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto text-sm leading-relaxed text-zinc-300" data-testid="settings-doc-body">
            {doc === "help" ? (
              <div className="space-y-4">
                {CONTACT_MSG && <p className="text-zinc-300">{CONTACT_MSG}</p>}
                {FAQ.map((f, i) => (
                  <div key={i}>
                    <div className="font-heading font-semibold text-[#F0E9DD]">{f.q}</div>
                    <p className="text-zinc-400 mt-1">{f.a}</p>
                  </div>
                ))}
                <p className="text-zinc-400">Still stuck? Email us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold underline">{SUPPORT_EMAIL}</a>.</p>
              </div>
            ) : (
              <p>{doc === "terms" ? TERMS : PRIVACY}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <BlockedAccountsDialog open={blocksOpen} onOpenChange={setBlocksOpen} />
      <BottomNav />
    </div>
  );
}

function BlockedAccountsDialog({ open, onOpenChange }) {
  const [list, setList] = useState(null);
  useEffect(() => { if (open) { setList(null); api.get("/me/blocks").then(({ data }) => setList(data)).catch(() => setList([])); } }, [open]);
  const unblock = async (u) => {
    try { await api.post(`/users/${u.id}/block`); setList((l) => l.filter((x) => x.id !== u.id)); toast.success(`Unblocked ${u.username || u.name}`); }
    catch { toast.error("Couldn't unblock"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink-900 border-zinc-800 text-[#F0E9DD] max-w-md" data-testid="blocked-dialog">
        <DialogHeader><DialogTitle className="font-heading font-black uppercase tracking-tight">Blocked accounts</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">People you have blocked from messaging you</DialogDescription>
        <div className="max-h-[60vh] overflow-y-auto">
          {list === null ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gold" size={22} /></div>
            : list.length === 0 ? <p className="text-center text-sm text-zinc-500 py-10" data-testid="blocked-empty">You haven't blocked anyone.</p>
            : <div className="space-y-1">
                {list.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 px-1 py-2" data-testid={`blocked-${u.id}`}>
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-ink-800 shrink-0">{u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : <User size={16} className="m-auto mt-2.5 text-zinc-500" />}</div>
                    <span className="flex-1 font-heading font-semibold text-sm truncate">{u.username || u.name}</span>
                    <button onClick={() => unblock(u)} data-testid={`unblock-${u.id}`} className="text-xs font-heading font-bold border border-zinc-700 rounded-full px-3 py-1.5 hover:border-gold hover:text-gold transition-colors">Unblock</button>
                  </div>
                ))}
              </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
